import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { retry } from 'rxjs/operators';

import { GetglobalsettingsService } from './getglobalsettings.service';
import { AgentTeamMember, AgentCustomerProfile, AgentCustomerProfileListRow } from '../shared/agent-admin';

/**
 * REST client for API-11 (Agent Admin API), `/api/agent/admin/*` on the
 * webapi backend — CRUD for `AgentTeamsIdentity`, `AgentCustomerProfiles`.
 * Every route is `realm:portal-admin` server-side; this service does not
 * itself gate access — the host components/pages do (see
 * docs/PROMPT_agent-admin-panels-refactor.md).
 *
 * Unlike some older services in this app (e.g. `document-api.service.ts`),
 * errors are NOT caught/alerted here — callers subscribe with their own
 * `error` handler and surface it via `MessageService` toast, matching
 * `edit-access-groups-tab.component.ts`'s pattern.
 */
@Injectable({
  providedIn: 'root'
})
export class AgentAdminApiService {

  constructor(
    private http: HttpClient,
    private globalsettingsservice: GetglobalsettingsService,
  ) {
  }

  /** Builds an absolute API URL by prepending the runtime API server address. */
  private _getApiUrl(url: string = ''): string {
    return this.globalsettingsservice.getApiServerUrl() + url;
  }

  /** Fresh standard headers per call so Setup-screen overrides take effect
   *  without a page reload (same reasoning as `document-api.service.ts`). */
  private _options() {
    return { headers: this.globalsettingsservice.getStandardApiHeaders() };
  }

  // ---------------------------------------------------------------------------
  // Team members
  // ---------------------------------------------------------------------------

  /** `GET /api/agent/admin/team-members` — list all team members, including inactive ones. */
  getTeamMembers(): Observable<AgentTeamMember[]> {
    return this.http.get<AgentTeamMember[]>(this._getApiUrl('/api/agent/admin/team-members'), this._options())
      .pipe(retry(1));
  }

  /** `GET /api/agent/admin/team-members/:personId` — single-person lookup, or
   *  `null` if that person has no `AgentTeamsIdentity` row yet (the common
   *  case). Used by the Person editor's own Teams-GUID field. */
  getTeamMember(personId: number): Observable<AgentTeamMember | null> {
    return this.http.get<AgentTeamMember | null>(this._getApiUrl('/api/agent/admin/team-members/' + personId), this._options())
      .pipe(retry(1));
  }

  /** `POST /api/agent/admin/team-members` — insert a new team member. Returns `{ person_id }`. */
  createTeamMember(member: Partial<AgentTeamMember>): Observable<{ person_id: number }> {
    const { display_name, uid, cloneIndex, ...body } = member as AgentTeamMember;
    return this.http.post<{ person_id: number }>(this._getApiUrl('/api/agent/admin/team-members'), body, this._options())
      .pipe(retry(1));
  }

  /** `PUT /api/agent/admin/team-members/:person_id` — update an existing team
   *  member, including `active` — this IS the deactivate action, not a
   *  separate call. `display_name` is a server-derived read-only field and
   *  is stripped before sending. */
  updateTeamMember(member: AgentTeamMember): Observable<{ person_id: number }> {
    const { display_name, uid, cloneIndex, ...body } = member;
    return this.http.put<{ person_id: number }>(this._getApiUrl('/api/agent/admin/team-members/' + member.person_id), body, this._options())
      .pipe(retry(1));
  }

  // ---------------------------------------------------------------------------
  // Customer tier
  // ---------------------------------------------------------------------------

  /** `GET /api/agent/admin/customer-tier/:cliId` — returns the profile row, or
   *  `null` if none exists yet (the common case — Phase 7-A seed only covers
   *  key-accounts and overrides). */
  getCustomerTier(cliId: number): Observable<AgentCustomerProfile | null> {
    return this.http.get<AgentCustomerProfile | null>(this._getApiUrl('/api/agent/admin/customer-tier/' + cliId), this._options())
      .pipe(retry(1));
  }

  /** `PUT /api/agent/admin/customer-tier/:cliId` — upsert. `tier_set_by`/
   *  `tier_set_at` are stripped before sending even if present on the input
   *  object — the backend derives both server-side and should never receive
   *  them from the client. Also strips `AgentCustomerProfileListRow`'s own
   *  extra fields (`customer_name`/`account_manager_name`/
   *  `override_company_name`/`tier_set_by_name` — server-joined display
   *  names, never writable — plus `uid`/`cloneIndex`, this UI's own
   *  row-editing bookkeeping) when a caller passes a list row straight
   *  through, same as `app-edit-agent-customer-tier-list-tab.onRowEditSave()`
   *  does: centralizing the strip here means that caller doesn't need to
   *  build its own stripped payload, and any future caller gets the same
   *  protection automatically. A no-op for the existing per-client-tab
   *  caller, whose plain `AgentCustomerProfile` never carried these fields
   *  to begin with. */
  saveCustomerTier(cliId: number, profile: Partial<AgentCustomerProfileListRow>): Observable<{ customer_id: number }> {
    const {
      tier_set_by, tier_set_at, customer_id,
      customer_name, account_manager_name, override_company_name, tier_set_by_name,
      uid, cloneIndex,
      ...body
    } = profile as AgentCustomerProfileListRow;
    return this.http.put<{ customer_id: number }>(this._getApiUrl('/api/agent/admin/customer-tier/' + cliId), body, this._options())
      .pipe(retry(1));
  }

  /** `GET /api/agent/admin/customer-tier` — the admin-wide list of every
   *  customer currently carrying a tier exception (`tier <> 'standard'`),
   *  enriched with display names server-side. Backs
   *  `app-edit-agent-customer-tier-list-tab`; see
   *  docs/PROMPT_agent-customer-tier-admin-list.md. Empty array is the
   *  normal "no exceptions" case, not an error. */
  getCustomerTierList(): Observable<AgentCustomerProfileListRow[]> {
    return this.http.get<AgentCustomerProfileListRow[]>(this._getApiUrl('/api/agent/admin/customer-tier'), this._options())
      .pipe(retry(1));
  }

  /** `DELETE /api/agent/admin/customer-tier/:cliId` — hard delete of that
   *  customer's `AgentCustomerProfiles` row (the customer reverts to
   *  `standard`/no-row). Used only by `app-edit-agent-customer-tier-list-tab`
   *  -- the per-client tab has no delete action of its own. */
  deleteCustomerTier(customerId: number): Observable<void> {
    return this.http.delete<void>(this._getApiUrl('/api/agent/admin/customer-tier/' + customerId), this._options())
      .pipe(retry(1));
  }
}
